import React, { useCallback, useEffect } from "react";
import { connect, useSelector } from "react-redux";
import { StepButtons, CustomInput } from "components";
import fp from "lodash/fp";
import { reduxForm, Field, change, getFormSyncWarnings } from "redux-form";
import styles from "../stepper.module.scss";
import { Row, Col } from "react-bootstrap";
import SelectAsyncForBankName from "components/select/selectAsyncForBankName";
import SelectAsyncForBankRouting from "components/select/selectAsyncForBankRouting";
import { dropdownValuesService } from "services";
import { getBanks } from "store/creditHistory/creditHistory.reducer";
import { creditHistoryActions, uiActions } from "store";
import {
	FORM_NAME,
	BANK_NAME_FIELD,
	BANK_ROUTING_FIELD,
	ACCOUNT_NUMBER_FIELD,
	validateBankRouting,
	validateAccountNumber,
	warnCreditHistory,
} from "./form";
import {
	maxLength50,
	normalizeAll,
	onlyNumberDate,
	trim,
	trimMidSentence,
	onlyAlphanumericAndSpecialCharacters,
} from "validators";

const CreditHistoryStep = ({
	prevStep,
	nextStep,
	submitting,
	handleSubmit,
	banks,
	dispatch,
	token,
	warnings,
}) => {
	useEffect(() => {
		if (submitting) {
			dispatch(uiActions.showSpinnerOverlay());
		}
		return () => dispatch(uiActions.hideSpinnerOverlay());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [submitting]);

	const PAGE_SIZE = 20;

	const isBankRouteExist = !warnings?.[BANK_ROUTING_FIELD];

	const handleSyncByName = bankName => {
		const bankWithName = fp.find(["bank", bankName], banks);
		if (bankWithName) {
			dispatch(
				uiActions.showSuccessNotification({
					title: "Routing and Bank have been updated",
				}),
			);
			dispatch(
				change(FORM_NAME, BANK_ROUTING_FIELD, bankWithName.routing),
			);
		}
	};

	const handleShowName = name => {
		if (!name) return null;
		const bankByName = fp.find(["bank", name], banks);

		return bankByName
			? { value: bankByName.bank, label: bankByName.bank }
			: { value: name, label: name };
	};

	const loadBankRoutings = useCallback(
		async (search, prevOptions, { page }) => {
			if (search.length < 3) {
				return {
					options: [
						{
							value: undefined,
							label: undefined,
						},
					],
				};
			} else {
				const body = {
					pattern: search,
					page,
					pageSize: PAGE_SIZE,
				};

				const { items, count } = await dropdownValuesService.getBanks(
					body,
				);
				const hasMore = prevOptions.length + items.length < count;
				const newBanks = fp.uniqBy("id", [...items]);

				if (fp.isEmpty(items)) {
					dispatch(creditHistoryActions.setBanks([]));
					return {
						options: [
							{
								value: search,
								name: "",
								id: null,
								label: search,
							},
						],
					};
				} else {
					dispatch(creditHistoryActions.setBanks(newBanks));
					return {
						options: newBanks.map(b => ({
							value: b.routing,
							name: b.bank,
							id: b.id,
							label: b.routing,
						})),
						hasMore,
						additional: {
							page: page + 1,
						},
					};
				}
			}
		},
		[dispatch],
	);

	const handleSyncByRouting = (bankRouting, { name, id }) => {
		if (!name) {
			dispatch(change(FORM_NAME, BANK_NAME_FIELD, ""));
		} else {
			dispatch(
				creditHistoryActions.setBanks([
					...banks,
					{ bank: name, id, routing: bankRouting },
				]),
			);
			dispatch(change(FORM_NAME, BANK_NAME_FIELD, name));
		}
	};

	const applicationId = useSelector(state => state.generalInfo.applicationId);

	const saveCreditHistory = values => {
		return dispatch(
			creditHistoryActions.sendCreditHistory({
				...values,
				token,
				applicationId,
			}),
		).then(nextStep);
	};

	return (
		<form
			onSubmit={handleSubmit(saveCreditHistory)}
			className={styles.form}
		>
			<Row>
				<Col lg={6} md={12}>
					{isBankRouteExist ? (
						<Field
							component={SelectAsyncForBankName}
							name={BANK_NAME_FIELD}
							findOption={handleShowName}
							onChangeCb={handleSyncByName}
							hideRequiredMark={true}
							readOnly={true}
							tabIndex={1}
						/>
					) : (
						<Field
							component={CustomInput}
							label={"Bank Name"}
							name={BANK_NAME_FIELD}
							hideRequiredMark
							validate={maxLength50}
							normalizeOnBlur={trim}
							normalize={normalizeAll([
								onlyAlphanumericAndSpecialCharacters,
								trimMidSentence,
							])}
						/>
					)}
					<Field
						component={SelectAsyncForBankRouting}
						name={BANK_ROUTING_FIELD}
						loadOptions={loadBankRoutings}
						onChangeCb={handleSyncByRouting}
						validate={validateBankRouting}
						normalize={onlyNumberDate}
						tabIndex={3}
					/>
				</Col>
				<Col lg={6} md={12}>
					<Field
						component={CustomInput}
						name={ACCOUNT_NUMBER_FIELD}
						label="Account Number"
						validate={validateAccountNumber}
						normalize={onlyNumberDate}
						tabIndex={2}
					/>
				</Col>
			</Row>
			<StepButtons
				nextStep={nextStep}
				prevStep={prevStep}
				initialTabIndex={4}
			/>
		</form>
	);
};

const mapStateToProps = state => ({
	banks: getBanks(state),
	warnings: getFormSyncWarnings(FORM_NAME)(state),
});

const creditHistory = fp.flow(
	reduxForm({
		form: FORM_NAME,
		destroyOnUnmount: false,
		warn: warnCreditHistory,
	}),
	connect(mapStateToProps),
)(CreditHistoryStep);

export { creditHistory as CreditHistoryStep };
